import React, { useMemo } from 'react';
import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import RefreshButton from '../common/RefreshButton';

import Sparkline from '../Sparkline';
import { DN_COST_TOOLTIP, RT_COST_TOOLTIP } from './fulfillmentCostTooltips';
import { formatInt, formatMoney } from '../../utils/format';
import { KPI_LABEL_SX } from '../../theme/tokens';

/**
 * FulfillmentCostKpiCard — rolling-30d cost-per-shipment KPI for DN or RT.
 *
 * @description
 *   Shows median (headline), mean (secondary), max + reference (subline), and
 *   a monthly trend sparkline (last 6 months) of total cost per month. Used on
 *   the home dashboard's Storage & Inventory section AND on the /fulfillment
 *   KPI row, wired to the same /api/fulfillment/kpis/rolling endpoint so the
 *   two views cannot drift apart.
 *
 *   Replaces two prior divergent calculations: latest-month mean (dashboard)
 *   and lifetime-top-2000 mean (fulfillment view). See plan
 *   docs/superpowers/plans/2026-05-19-fulfillment-rolling-30d-kpis.md.
 *
 *   Sparkline is monthly, not daily — LSM batch-uploads invoices on a single
 *   day, so daily granularity produced single-day spikes that read as anomalies
 *   even though they were just upload-day artifacts. Monthly grain matches the
 *   other /fulfillment sparklines (last 6 months).
 *
 * @useWhen
 *   - Surfacing rolling-window cost-per-shipment on any dashboard tile.
 *   - Showing both a typical-value (median) and ceiling (max) at once.
 *   - Need a monthly trend sparkline alongside the rolling snapshot.
 *
 * @dontUseFor
 *   - Lifetime totals — use the fulfillment "Overall cost" KPI instead.
 *   - Per-day granularity — LSM batch upload pattern makes daily misleading.
 *   - Cost components (warehouse vs transport) — this card shows a single
 *     unified cost metric per ref_kind.
 *
 * @related
 *   - StatTile — generic card; this one's richer layout (median + mean + max)
 *     didn't fit the single-value StatTile shape.
 *   - Sparkline — embedded for the 6-month monthly trend.
 *
 * @reuseHints
 *   - Future cost-of-RMA card could reuse the same shape.
 *   - Inventory-aging board if we ever surface "max-aged-batch" similar pattern.
 *
 * @props
 *   kind: 'DN' | 'RT'
 *   data: rolling-block shape { shipments, mean_cost, median_cost, max_cost,
 *         max_reference, monthly: [{month, shipments, total_cost}] } or null
 *   loading: boolean — render skeleton if true
 *   error: string | null — render unavailable state if truthy
 *   onRetry: () => void — refresh handler shown in error state
 *   windowDays: number — headline window, defaults to 30
 *   trendMonths: number — sparkline trend window, defaults to 6
 */
export default function FulfillmentCostKpiCard({
  kind,
  data,
  loading = false,
  error = null,
  onRetry,
  windowDays = 30,
  trendMonths = 6,
}) {
  const theme = useTheme();
  const isDN = kind === 'DN';
  const label = isDN
    ? `Ø DN cost per shipment (last ${windowDays}d)`
    : `Ø RT handling cost (last ${windowDays}d)`;
  const tooltip = isDN ? DN_COST_TOOLTIP : RT_COST_TOOLTIP;

  const series = useMemo(
    () => buildMonthlySeries(data?.monthly, trendMonths),
    [data?.monthly, trendMonths]
  );

  if (loading) {
    return <FulfillmentCostKpiCardSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        {/* 🔴 F5-10: derselbe Etikett-Text wie im Normalzustand — hier stand
            noch ein nacktes `noWrap` ohne `Tooltip` daneben. Auf dem Telefon
            war der Fehlerzustand damit der einzige, in dem das Etikett
            abgeschnitten UND unerreichbar war (kein Tooltip, kein Aufklappen). */}
        <Typography variant="caption" color="text.secondary" sx={KPI_LABEL_SX}>
          {label}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Cost data unavailable
          </Typography>
          {/* E8 (2026-10-07): Symbol-Retry mit Auffrisch-Symbol — dieselbe
              Handlung wie ein Refresh, also derselbe Baustein. Der Text bleibt
              „Retry", weil die Karte in einem FEHLERZUSTAND steht: der Leser
              muss wissen, was wiederholt wird. Die beschrifteten Retries in
              Leer-/Fehlerkarten (`ProductsTable.jsx`, `emptyState`) bleiben
              unveraendert ein `Button`. */}
          {onRetry && (
            <RefreshButton
              ariaLabel="Retry loading cost data"
              tooltip="Retry loading cost data"
              onClick={onRetry}
            />
          )}
        </Stack>
      </Box>
    );
  }

  const shipments = Number(data?.shipments || 0);
  if (shipments === 0) {
    return (
      <Box sx={{ width: '100%' }}>
        <Tooltip title={tooltip} arrow>
          <Typography
            variant="caption"
            color="text.secondary"
            // 🔴 F5-10: `noWrap` + Ellipsis ist hier weg — das Etikett bricht
            //    schmal und auf grobem Zeiger um (`KPI_LABEL_SX`, Begruendung
            //    an der Fundstelle unten).
            sx={{ ...KPI_LABEL_SX, cursor: 'help' }}
          >
            {label}
          </Typography>
        </Tooltip>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          No {isDN ? 'shipments' : 'returns'} in window
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Tooltip title={tooltip} arrow>
        <Typography
          variant="caption"
          color="text.secondary"
          // 🔴 F5-10 (UI-Durchgang 2 Teil 5, 2026-10-09): `noWrap` + Ellipsis
          //    ist hier WEG. Das Etikett ist lang („Ø DN cost per shipment
          //    (last 30d)"), sein Volltext stand nur im `Tooltip` — und ein
          //    Tooltip oeffnet auf Touch nicht. Auf dem Telefon war es damit
          //    abgeschnitten UND unerreichbar. `KPI_LABEL_SX` bricht schmal und
          //    auf grobem Zeiger um und ellipsiert ab `sm` wie zuvor.
          //    Dieselbe Form traegt der geteilte `StatTile`.
          sx={{ ...KPI_LABEL_SX, cursor: 'help' }}
        >
          {label}
        </Typography>
      </Tooltip>

      <Typography variant="h6" sx={{ lineHeight: 1.25, mt: 0.5 }}>
        {formatEur(data.median_cost)} <Typography component="span" variant="caption" color="text.secondary">median</Typography>
      </Typography>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        mean {formatEur(data.mean_cost)} · max {formatEur(data.max_cost)}
        {data.max_reference ? ` · ${data.max_reference}` : ''}
      </Typography>

      {series.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Sparkline
            data={series}
            height={30}
            stroke={isDN ? undefined : theme.palette.warning.main}
          />
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        {formatInt(shipments)} {isDN ? 'shipments' : 'returns'} · last {trendMonths} months
      </Typography>
    </Box>
  );
}

export function FulfillmentCostKpiCardSkeleton() {
  return (
    <Box sx={{ width: '100%' }}>
      <Skeleton variant="text" width="65%" height={16} />
      <Skeleton variant="text" width="45%" height={28} sx={{ mt: 0.5 }} />
      <Skeleton variant="text" width="80%" height={14} />
      <Skeleton variant="rectangular" width="100%" height={30} sx={{ mt: 1, borderRadius: 0.5 }} />
      <Skeleton variant="text" width="30%" height={14} sx={{ mt: 0.5 }} />
    </Box>
  );
}

// Hausform statt `Intl.NumberFormat('en-GB')` (Regel-16-Gate B1/H2, 2026-10-06).
const formatEur = (value) => formatMoney(value);

/**
 * Zero-fill the monthly array to exactly `trendMonths` entries ending in the
 * current calendar month. Server returns only months that had shipments; the
 * sparkline needs a contiguous monthly series so visual gaps map to real
 * zero-months rather than missing data.
 *
 * Server returns ISO-date strings keyed to the first of the month
 * (DATE_TRUNC('month', ...)). The client constructs the same first-of-month
 * keys to match.
 */
export function buildMonthlySeries(monthly, trendMonths = 6) {
  const months = Math.max(1, Number(trendMonths) || 6);
  const byMonth = new Map();
  (Array.isArray(monthly) ? monthly : []).forEach((entry) => {
    if (entry && entry.month) {
      byMonth.set(entry.month, Number(entry.total_cost || 0));
    }
  });

  const series = [];
  const today = new Date();
  const baseYear = today.getFullYear();
  const baseMonth = today.getMonth(); // 0-indexed
  for (let i = months - 1; i >= 0; i -= 1) {
    const d = new Date(baseYear, baseMonth - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    series.push({ x: key, y: byMonth.get(key) ?? 0 });
  }
  return series;
}
